/**
 * Firebase error codes, said in words the person reading them can act on.
 *
 * Written after a colleague's first upload was refused and the screen showed
 * him this, verbatim:
 *
 *   Firebase Storage: User does not have permission to access
 *   'users/ASNamBCRV4RyRr6bRIqRiBoF1lw2/receipts/5hLG1hjy4fzIgvNdHgCQ/original.pdf'.
 *   (storage/unauthorized)
 *
 * Every word of that is true and none of it is useful. It names a path he has
 * no way to check, it reads as though his file or his account is broken, and
 * it gives no hint of what would resolve it — which was a permission on the
 * project, fixed in a minute by someone else once he thought to forward a
 * screenshot.
 *
 * The person most likely to meet one of these messages is the person least
 * equipped to interpret it, which is the whole argument for translating them.
 *
 * Two rules here:
 *
 * - **Say what to do, and no more.** "Ask for your access to be checked" is
 *   actionable. The error code is not, so it is kept only where nothing better
 *   can be said — an unrecognised failure still has to be reportable.
 * - **No names.** Who administers this will change; a message that names a
 *   person goes stale the day they hand it over, and is read by people outside
 *   the district too.
 */

interface CodedError {
  code?: unknown
  message?: unknown
}

/**
 * Firebase puts its code on the error object; everything else falls through.
 *
 * Exported because `problemReports` needs the same answer. A second copy of
 * this drifted into that file, and two copies mean the code shown to the
 * person and the code attached to their report can disagree.
 */
export function codeOf(error: unknown): string {
  const code = (error as CodedError | null)?.code
  return typeof code === 'string' ? code : ''
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message
  const message = (error as CodedError | null)?.message
  return typeof message === 'string' ? message : ''
}

/**
 * Worded for every screen, not for uploads.
 *
 * These started on the upload path and are now reached from the dashboard, the
 * budget codes, the reconcile screen and the problem list. "Mention that it
 * was refused on a receipt upload" then pointed whoever was asked to help at
 * the wrong place entirely, and "the file is still on your device" named a
 * file where none was involved. The caller's own `fallback` says which action
 * failed; these say what to do about it.
 */
const NO_PERMISSION =
  "Your account doesn't have permission for this. Nothing is wrong with what " +
  'you did — ask for your Budget Monkey access to be checked, and say which ' +
  'screen you were on when it was refused.'

const SIGNED_OUT = 'Your sign-in has expired. Reload the page and sign in again.'

const NO_CONNECTION =
  "This couldn't reach the server. Check your connection and try again — " +
  'nothing has been lost.'

const TOOK_TOO_LONG =
  'That took too long to come back. Check your connection and try again — ' +
  'nothing has been lost.'

/**
 * A sentence to show instead of a raw Firebase error.
 *
 * `fallback` is what to say when the failure isn't one of the known ones. It
 * should name the action that failed ("Couldn't load your receipts"), because
 * the code is appended to it and a bare code tells the reader nothing.
 */
export function humanErrorMessage(error: unknown, fallback: string): string {
  const code = codeOf(error)

  switch (code) {
    case 'storage/unauthorized':
    case 'permission-denied':
    case 'functions/permission-denied':
      return NO_PERMISSION

    case 'storage/unauthenticated':
    case 'unauthenticated':
    case 'functions/unauthenticated':
      return SIGNED_OUT

    case 'unavailable':
    case 'storage/retry-limit-exceeded':
    case 'functions/unavailable':
      return NO_CONNECTION

    // `ReadTimedOut` from `withDeadline` carries Firestore's own code for the
    // same condition. Without this case it fell to the default and showed
    // "Couldn't load your receipts. (deadline-exceeded)" — the raw code, on
    // the exact screens the deadline was added for.
    case 'deadline-exceeded':
    case 'functions/deadline-exceeded':
      return TOOK_TOO_LONG

    case 'storage/quota-exceeded':
      return 'There is no storage space left for new receipts. This one needs sorting out before any more can be saved.'

    case 'storage/canceled':
      return 'That upload was cancelled.'

    case 'resource-exhausted':
      return 'The app has hit a usage limit for now. Wait a few minutes and try again.'

    default: {
      // An unrecognised failure still has to be reportable, so the code goes
      // in — it is the one part worth forwarding. The raw message is dropped:
      // it is where the storage paths and internal ids live.
      const message = messageOf(error)
      if (code !== '') return `${fallback} (${code})`
      return message === '' ? fallback : `${fallback} (${message})`
    }
  }
}
